// hooks/useEmployeeProfileLogic.js
import { useAuth } from 'components/ui/navigationGuard';
import PersonalInfoTab from 'pages/employee-profile/components/personalInfoTab';
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import employeeDashboardApi from 'services/api/employeeDashboardApi';

const useEmployeeProfileLogic = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  const [isLoading, setIsLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(true);
  const [employee, setEmployee] = useState({});

  // Get the current user's role and employee ID
  const { user } = useAuth();
  const currentUserRole = user?.role;
  const isAdmin = currentUserRole === 'admin';
  const employeeId = searchParams?.get('id') || user?.employee_id;

  // Fetch departments from API
  const fetchDepartments = async () => {
    try {
      setLoadingDepartments(true);
      const departmentsData = await employeeDashboardApi.getDepartments();
      setDepartments(departmentsData);
    } catch (error) {
      console.error("Error fetching departments:", error);
    } finally {
      setLoadingDepartments(false);
    }
  };

  // Fetch employee from API
  const loadEmployee = async () => {
    try {
      setIsLoading(true);
      const employeeData = await employeeDashboardApi.getEmployee(employeeId);
      setEmployee(employeeData);
    } catch (error) {
      console.error("Error fetching employee:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchDepartments();
    loadEmployee();
  }, [employeeId]);

  // Update profile with data and/or files
  const handleUpdateProfile = async ({ data = {}, files = [] } = {}) => {
    try {
      let response;

      if (files.length > 0) {
        // Case 1: uploading files + profile data → multipart/form-data
        response = await employeeDashboardApi.updateProfileWithFiles(
          employee.employee_id,
          data,
          files
        );
      } else {
        // Case 2: just profile data → application/json
        response = await employeeDashboardApi.updateProfile(
          employee.employee_id,
          data
        );
      }

      loadEmployee();
      showNotification("Profile updated successfully", "success");
    } catch (error) {
      console.error("Error updating profile:", error);
      showNotification("Failed to update profile", "error");
    }
  };

  // Handle status change
  const handleStatusChange = (newStatus) => {
    setEmployee((prev) => ({
      ...prev,
      status: newStatus,
      updatedAt: new Date().toISOString(),
    }));
    showNotification(`Employee status changed to ${newStatus}`, 'success');
  };

  // Tab definitions
  const tabs = [
    { id: 'personal', label: 'Personal Information', icon: 'User', component: PersonalInfoTab },
    // { id: 'audit', label: 'Audit History', icon: 'FileText', component: AuditHistoryTab, adminOnly: true },
  ];

  // Filter tabs based on user role
  const availableTabs = tabs.filter(tab => !tab.adminOnly || isAdmin);
  const ActiveTabComponent = availableTabs.find(tab => tab.id === activeTab)?.component;

  // Notification functions
  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleToggleCollapse = () => setIsCollapsed(!isCollapsed);

  return {
    // State
    isCollapsed,
    activeTab,
    isLoading,
    notification,
    departments,
    loadingDepartments,
    employee,
    isAdmin,
    currentUserRole,
    employeeId,
    tabs,
    availableTabs,
    ActiveTabComponent,
    
    // State setters
    setIsCollapsed,
    setActiveTab,
    setEmployee,
    
    // Functions
    fetchDepartments,
    loadEmployee,
    handleUpdateProfile,
    handleStatusChange,
    showNotification,
    handleToggleCollapse,
    user
  };
};

export default useEmployeeProfileLogic;